import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Button,
  Dialog,
  Field,
  Heading,
  Input,
  Label,
  Paragraph,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import { PlusIcon, TrashIcon } from '@navikt/aksel-icons';
import { TeamLeader } from '../../api/queries';
import { useGetTeamLeaders } from '../../hooks/queries/useGetTeamLeaders';
import { useAddTeamLeaderMutation } from '../../hooks/mutations/useAddTeamLeaderMutation';
import { useDeleteTeamLeaderMutation } from '../../hooks/mutations/useDeleteTeamLeaderMutation';
import { GhostLoader } from '../GhostLoader';
import { SettingsSection } from '../TeamSettingsLayout';
import { SettingsListItem } from '../TeamSettingsLayout/SettingsListItem';
import classes from './TeamAccessCard.module.css';

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

export const TeamAccessCard = () => {
  const { t } = useTranslation();
  const { teamId } = useParams();
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [userToDelete, setUserToDelete] = useState<TeamLeader | null>(null);

  const { data: teamLeaders = [], isLoading, error } = useGetTeamLeaders(Number(teamId));
  const { mutate: addTeamLeader, isPending: isInviting } = useAddTeamLeaderMutation(Number(teamId));
  const { mutate: deleteTeamLeader, isPending: isDeleting } = useDeleteTeamLeaderMutation(
    Number(teamId),
  );

  const openInvite = () => {
    setEmail('');
    setEmailError(null);
    setInviteError(null);
    setIsInviteOpen(true);
  };

  const onInvite = (event: FormEvent) => {
    event.preventDefault();
    const value = email.trim();
    if (!EMAIL_PATTERN.test(value)) {
      setEmailError(t('teamSettings.channels.emailInvalid'));
      return;
    }

    addTeamLeader(value, {
      onSuccess: () => {
        setIsInviteOpen(false);
        toast.success(t('addUserCard.invitationSent'), { position: 'bottom-right' });
      },
      onError: () => setInviteError(t('teamSettings.leaders.inviteError')),
    });
  };

  const onDeleteTeamLeader = () => {
    if (!userToDelete) return;
    deleteTeamLeader(userToDelete.id, {
      onSuccess: () => {
        setUserToDelete(null);
        toast.success(t('teamAccessCard.userDeleted'), { position: 'bottom-right' });
      },
    });
  };

  if (isLoading) {
    return <GhostLoader style={{ height: 240, width: '100%' }} />;
  }

  if (error) {
    return (
      <Alert data-color="danger">
        <Paragraph>{error.message}</Paragraph>
      </Alert>
    );
  }

  return (
    <>
      <SettingsSection
        title={t('teamSettings.leaders.title')}
        description={t('teamSettings.leaders.description')}
        action={
          <Button data-size="sm" onClick={openInvite}>
            <PlusIcon aria-hidden="true" />
            {t('teamSettings.leaders.add')}
          </Button>
        }
      >
        {teamLeaders.length > 0 ? (
          <ul className={classes.list}>
            {teamLeaders.map((user) => (
              <SettingsListItem
                key={user.userId}
                initial={user.email.charAt(0).toUpperCase()}
                title={user.email}
                subtitle={
                  user.pending ? t('teamSettings.leaders.invited') : t('teamSettings.leaders.active')
                }
                action={
                  <Button
                    variant="tertiary"
                    data-color="danger"
                    data-size="sm"
                    aria-label={t('teamSettings.leaders.removeLabel', { email: user.email })}
                    onClick={() => setUserToDelete(user)}
                  >
                    <TrashIcon aria-hidden="true" />
                    {t('teamSettings.common.remove')}
                  </Button>
                }
              />
            ))}
          </ul>
        ) : (
          <Paragraph data-size="sm" className={classes.subtle}>
            {t('teamSettings.leaders.empty')}
          </Paragraph>
        )}
      </SettingsSection>

      <Dialog
        open={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        closedby="any"
        closeButton={false}
        aria-labelledby="invite-leader-heading"
        className={classes.dialog}
      >
        <form className={classes.dialogContent} onSubmit={onInvite} noValidate>
          <Heading id="invite-leader-heading" level={2} data-size="xs">
            {t('teamSettings.leaders.inviteTitle')}
          </Heading>
          <Field>
            <Label htmlFor="invite-leader-email">{t('teamSettings.leaders.inviteLabel')}</Label>
            <Paragraph data-size="sm" id="invite-leader-email-description" className={classes.subtle}>
              {t('teamSettings.leaders.inviteDescription')}
            </Paragraph>
            <Input
              id="invite-leader-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              aria-describedby="invite-leader-email-description"
              aria-invalid={emailError ? true : undefined}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError(null);
                setInviteError(null);
              }}
            />
            {emailError && <ValidationMessage>{emailError}</ValidationMessage>}
          </Field>
          {inviteError && (
            <Alert data-color="danger">
              <Paragraph>{inviteError}</Paragraph>
            </Alert>
          )}
          <div className={classes.dialogActions}>
            <Button type="submit" disabled={isInviting}>
              {t('teamSettings.leaders.inviteButton')}
            </Button>
            <Button variant="tertiary" onClick={() => setIsInviteOpen(false)}>
              {t('teamSettings.common.cancel')}
            </Button>
          </div>
        </form>
      </Dialog>

      <Dialog
        open={userToDelete !== null}
        onClose={() => setUserToDelete(null)}
        closedby="any"
        closeButton={false}
        aria-labelledby="remove-leader-heading"
        className={classes.dialog}
      >
        <div className={classes.dialogContent}>
          <Heading id="remove-leader-heading" level={2} data-size="xs">
            {t('teamSettings.leaders.removeTitle', { email: userToDelete?.email ?? '' })}
          </Heading>
          <Paragraph>{t('teamSettings.leaders.removeDescription')}</Paragraph>
          <div className={classes.dialogActions}>
            <Button data-color="danger" onClick={onDeleteTeamLeader} disabled={isDeleting}>
              {t('teamSettings.common.remove')}
            </Button>
            <Button variant="tertiary" onClick={() => setUserToDelete(null)}>
              {t('teamSettings.common.cancel')}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};
